/**
 * WEB-1106 — the one answer to "does `VenueUpcomingShows` render anything?".
 *
 * Lives outside `VenueUpcomingShows.tsx` on purpose: that module is
 * `'use client'`, and a plain function exported from a client module reaches a
 * Server Component as a client reference it cannot call. `VenueDetailTemplate`
 * is a Server Component and has to ask this question during render.
 *
 * @since WEB-1106
 */
import type { VenueShow } from '@/types/venueDetail';

/** What decides whether the section renders at all. */
export interface VenueUpcomingShowsContentInput {
  readonly shows: ReadonlyArray<VenueShow>;
  readonly unavailable?: boolean;
}

/**
 * Whether the organism renders anything. A degraded read with no rows renders
 * NOTHING (API-1693): we did not learn the calendar is empty, we failed to read
 * it, and silence is not a claim. Rows that did arrive are real and render, and
 * a calendar that was read and is empty gets its "Nothing on the calendar yet"
 * state.
 *
 * `VenueDetailTemplate` wraps the organism in a `<section>` flex item, which
 * has to go when this is false, or an empty item adds a second `gap-10`
 * between About and Amenities. `VenueUpcomingShowsView` asks the same
 * predicate, so the template and the organism cannot disagree — the pattern
 * `venueRightRailHasContent` set for the rail track.
 */
export function venueUpcomingShowsHasContent({
  shows,
  unavailable = false,
}: VenueUpcomingShowsContentInput): boolean {
  return shows.length > 0 || !unavailable;
}
